import { useEffect, useRef, useState } from "react";
import date from "date-and-time";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { UseMutationResult } from "@tanstack/react-query";

import { Alert } from "../../../components/base/Alert";
import { Button } from "../../../components/base/Button";
import {
  ITicketInsightAnalyseRequest,
  ITicketInsightAnalyseResult,
  ITicketInsightQuery,
  ITicketInsightStatus,
} from "../../../models/Tickets/TicketInsight";

interface AnalyseCardProps {
  query: ITicketInsightQuery;
  status?: ITicketInsightStatus;
  isLoading: boolean;
  analyse: UseMutationResult<
    ITicketInsightAnalyseResult,
    Error,
    ITicketInsightAnalyseRequest
  >;
  onComplete: () => void;
}

const BATCH_SIZE = 40;

interface RunState {
  running: boolean;
  analysed: number;
  failed: number;
  target: number;
  message?: string;
  error?: string;
}

const idleRun: RunState = { running: false, analysed: 0, failed: 0, target: 0 };

/**
 * Coverage of the model over the current scope, plus the button that runs it.
 * The API analyses a bounded batch per call, so this loops until nothing is
 * pending and reports progress as it goes.
 */
export default function AnalyseCard({
  query,
  status,
  isLoading,
  analyse,
  onComplete,
}: AnalyseCardProps) {
  const [run, setRun] = useState<RunState>(idleRun);
  const cancelled = useRef(false);

  // Stop looping if the page is left mid-run.
  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  const startRun = async (force: boolean) => {
    if (!status) return;

    const target = force ? status.totalInScope : status.pending;
    setRun({ running: true, analysed: 0, failed: 0, target });

    let analysed = 0;
    let failed = 0;
    let remaining = target;
    let message: string | undefined;
    let error: string | undefined;
    let first = true;

    try {
      while (remaining > 0 && !cancelled.current) {
        const result = await analyse.mutateAsync({
          customerId: query.customerId,
          days: query.days,
          includeResolved: query.includeResolved,
          maxTickets: BATCH_SIZE,
          // Force only makes sense on the first batch; after that the hashes are fresh and
          // "pending" naturally means "whatever the first pass didn't get to".
          force: force && first,
        });
        first = false;

        analysed += result.analysed;
        failed += result.failed;
        remaining = result.remaining;
        message = result.message;

        setRun({ running: true, analysed, failed, target, message });

        if (result.stopped) {
          error = result.message;
          break;
        }
        // Nothing analysed and nothing failed means the loop would spin; bail.
        if (result.analysed === 0 && result.failed === 0) break;
        // Everything left failed; don't retry the same tickets forever.
        if (result.analysed === 0 && result.failed > 0) {
          error = "Some tickets could not be analysed. See the API logs.";
          break;
        }
      }
    } catch (e) {
      error = e instanceof Error ? e.message : "Analysis failed";
    }

    setRun({ running: false, analysed, failed, target, message, error });
    onComplete();
  };

  const handleReanalyse = () => {
    if (!status) return;
    if (
      window.confirm(
        `Re-analyse all ${status.totalInScope} tickets in scope? This re-runs the model over tickets that are already up to date and will use OpenAI credit.`,
      )
    ) {
      startRun(true);
    }
  };

  if (isLoading && !status) {
    return <div className="h-24 animate-pulse rounded-md bg-gray-100" />;
  }

  if (!status) return null;

  if (!status.isAiConfigured) {
    return (
      <Alert
        severity="warn"
        title="AI is not configured"
        message="OpenAI:ApiKey is not set on this environment, so tickets cannot be analysed. Existing insights (if any) are still shown below."
      />
    );
  }

  const coverage =
    status.totalInScope === 0
      ? 0
      : Math.round((status.analysed / status.totalInScope) * 100);

  const progress =
    run.target === 0
      ? 0
      : Math.min(100, Math.round(((run.analysed + run.failed) / run.target) * 100));

  return (
    <div className="rounded-md border border-orange-200 bg-orange-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-[16rem] flex-1">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-orange-600" />
            <h2 className="text-sm font-semibold text-slate-800">
              AI coverage
            </h2>
          </div>
          <p className="mt-1 text-sm text-slate-700">
            <span className="font-semibold">{status.analysed}</span> of{" "}
            <span className="font-semibold">{status.totalInScope}</span>{" "}
            tickets in scope analysed ({coverage}%)
            {status.pending > 0 ? (
              <>
                {" "}
                · <span className="font-semibold">{status.pending}</span> new or
                edited since their last analysis
              </>
            ) : (
              <> · everything is up to date</>
            )}
          </p>
          <div className="mt-2 h-2 overflow-hidden rounded bg-white">
            <div
              className="h-full rounded bg-orange-500 transition-all"
              style={{ width: `${run.running ? progress : coverage}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-gray-600">
            {run.running
              ? `Analysing… ${run.analysed + run.failed} of ${run.target}${
                  run.failed ? ` (${run.failed} failed)` : ""
                }`
              : status.lastAnalysedAt
                ? `Last analysed ${date.format(
                    new Date(status.lastAnalysedAt),
                    "DD/MM/YY HH:mm",
                  )}`
                : "Nothing has been analysed yet"}
          </p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <Button
            onClick={() => startRun(false)}
            disabled={run.running || status.pending === 0}
            className="flex items-center gap-2"
          >
            <SparklesIcon className="h-4 w-4" />
            {run.running
              ? "Analysing…"
              : status.pending > 0
                ? `Analyse ${status.pending} pending`
                : "Up to date"}
          </Button>
          <button
            type="button"
            onClick={handleReanalyse}
            disabled={run.running || status.totalInScope === 0}
            className="text-xs text-gray-600 underline hover:text-gray-900 disabled:text-gray-400 disabled:no-underline"
          >
            Re-analyse everything in scope
          </button>
        </div>
      </div>

      {!run.running && run.error ? (
        <Alert severity="error" message={run.error} className="mt-3" />
      ) : null}
      {!run.running && !run.error && run.message && run.target > 0 ? (
        <Alert severity="success" message={run.message} className="mt-3" />
      ) : null}
    </div>
  );
}
